import { useState, useMemo, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/common/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { Phone, MessageCircle, Star, Search, Users, User, AlertTriangle, ChevronRight, UserPlus, Loader2, FileText, X, Mail, CreditCard, SlidersHorizontal, ChevronDown, MapPin, Map, ExternalLink, RotateCcw, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import type { SMContact, BankerDocument } from "@/types";
import { cn } from "@/lib/utils";
import { useBankers } from "@/hooks/useBankers";
import { useLeads } from "@/hooks/useLeads";
import { useTasks } from "@/hooks/useTasks";
import { useAppConfig } from '@/hooks/useAppConfig';
import { useFileUpload } from '@/hooks/useFileUpload';
import { FileUploadSection } from '@/components/common/FileUploadSection';
import { MapLocationPicker } from "@/components/common/MapLocationPicker";
import { INDIAN_CITIES } from "@/lib/constants";
import { Bell } from "lucide-react";
import { searchItems } from "@/lib/searchUtils";
import { useModal } from "@/contexts/ModalContext";


const DESIGNATION_OPTIONS = [
  'National Head',
  'Zonal Manager',
  'Regional Manager',
  'Area Sales Manager',
  'Sales Manager',
  'Other',
];

export default function SMContactPage() {
  const navigate = useNavigate();
  const { bankers, loading, addBanker, updateBanker, toggleFavorite, isFavorite, reportBanker, uploadDocument, checkExists } = useBankers();
  const { leads } = useLeads();
  const { tasks } = useTasks();
  const { config } = useAppConfig();
  const { registerModal, unregisterModal } = useModal();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterExpanded, setFilterExpanded] = useState(false);
  const [filterBanks, setFilterBanks] = useState<string[]>([]);
  const [filterProducts, setFilterProducts] = useState<string[]>([]);
  const [filterLocations, setFilterLocations] = useState<string[]>([]);
  const [bankSearch, setBankSearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [locationSearch, setLocationSearch] = useState("");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null);
  const [reportReason, setReportReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customDesignation, setCustomDesignation] = useState('');
  const [isOtherDesignation, setIsOtherDesignation] = useState(false);

  // Unique regions from actual banker data for the Location filter
  const uniqueLocations = useMemo(() => {
    const regions = new Set(bankers.map(b => b.region).filter(Boolean));
    return Array.from(regions).sort();
  }, [bankers]);

  const activeFilterCount = filterBanks.length + filterProducts.length + filterLocations.length;
  
  const cityOptions = INDIAN_CITIES.map(city => ({ value: city, label: city }));
  const bankOptions = config.banks.map(bank => ({ value: bank, label: bank }));
  
  // Count leads and reminders per banker
  const getLeadCount = (bankerId: string) => leads.filter(l => l.smContactId === bankerId).length;
  const getReminderCount = (bankerId: string) => tasks.filter(t => t.smContactId === bankerId && !t.completed).length;
  
  const fileUploadManager = useFileUpload({
    maxSizeMB: config.maxFileUploadSizeMB,
    maxFiles: config.maxFileUploads,
    allowedFileTypes: config.allowedFileTypes,
    uploadMode: 'deferred',
  });

  // Alias pending files as 'files' to keep the rest of the page unchanged
  const fileManagerFiles = fileUploadManager.pendingFiles;
  const fileManagerReset = fileUploadManager.resetPending;
  
  const [newContact, setNewContact] = useState({
    name: "",
    bank: "",
    phone: "",
    email: "",
    region: "",
    designation: "",
    products: [] as string[],
    remark: "",
  });
  const [newContactLocation, setNewContactLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);

  const handleToggleFavorite = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await toggleFavorite(id);
    } catch (error) {
      toast.error("Failed to update favorite status");
    }
  };

  const handleProductToggle = (productValue: string) => {
    setNewContact(prev => ({
      ...prev,
      products: prev.products.includes(productValue)
        ? prev.products.filter(p => p !== productValue)
        : [...prev.products, productValue]
    }));
  };

  const handleAddContact = async () => {
    if (!newContact.name || !newContact.phone || !newContact.bank) {
      toast.error("Please fill required fields");
      return;
    }

    if (newContact.phone.replace(/\D/g, '').length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    // Check for duplicate phone number
    try {
      const exists = await checkExists(newContact.phone);
      if (exists) {
        toast.error("A banker with this mobile number already exists");
        return;
      }
    } catch (error) {
      console.error('Error checking duplicate:', error);
      toast.error("Failed to check for duplicates");
      return;
    }

    // Soft warning if no products selected (still allows save)
    if (newContact.products.length === 0) {
      toast.warning("No loan products selected. You can add them later from the banker's profile.", { duration: 4000 });
    }

    setIsSubmitting(true);

    try {
      // Race the addBanker call against a 30-second timeout to prevent indefinite hang
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out after 30 seconds')), 30000)
      );
      // First, add the banker without documents
      const banker = await Promise.race([addBanker({
        ...newContact,
        products: newContact.products,
        ...(newContactLocation ? { location: newContactLocation } : {}),
        createdBy: "", // Will be set by the hook
        createdAt: new Date(),
        documents: [],
      }), timeoutPromise]);

      // Upload documents if any
      if (fileManagerFiles.length > 0) {
        toast.info(`Uploading ${fileManagerFiles.length} file${fileManagerFiles.length > 1 ? 's' : ''}...`);

        const uploadResults = await Promise.allSettled(
          fileManagerFiles.map(file => uploadDocument(file, banker.id))
        );

        const uploadedDocs: BankerDocument[] = [];
        const failedNames: string[] = [];
        uploadResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            uploadedDocs.push(result.value);
          } else {
            console.error('Upload error:', result.reason);
            failedNames.push(fileManagerFiles[index].name);
          }
        });

        if (uploadedDocs.length > 0) {
          await updateBanker(banker.id, { documents: uploadedDocs });
        }

        if (failedNames.length === 0) {
          toast.success(`${uploadedDocs.length} file${uploadedDocs.length > 1 ? 's' : ''} uploaded successfully`);
        } else if (uploadedDocs.length > 0) {
          toast.warning(`${uploadedDocs.length} of ${fileManagerFiles.length} files uploaded. Failed: ${failedNames.join(', ')}`);
        } else {
          toast.error(`All uploads failed. Banker saved — add documents from detail page.`);
        }
      }

      toast.success("Banker Contact added successfully");
      setAddDialogOpen(false);
      setNewContact({ name: "", bank: "", phone: "", email: "", region: "", designation: "", products: [], remark: "" });
      setNewContactLocation(null);
      setIsOtherDesignation(false);
      setCustomDesignation('');
      fileManagerReset();
    } catch (error) {
      console.error('Error adding banker:', error);
      const isTimeout = error instanceof Error && error.message.includes('timed out');
      toast.error(isTimeout
        ? "Request timed out. Please check your connection and try again."
        : error instanceof Error ? error.message : 'Failed to add banker contact'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenReport = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    setSelectedContactId(id);
    setReportReason("");
    setReportDialogOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!reportReason.trim()) {
      toast.error("Please enter a report reason");
      return;
    }
    if (!selectedContactId) return;

    setIsSubmitting(true);
    try {
      await reportBanker(selectedContactId, reportReason.trim());
      toast.success("Report submitted. Thank you for your feedback.");
      setReportDialogOpen(false);
      setSelectedContactId(null);
      setReportReason("");
    } catch {
      toast.error("Failed to submit report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Register Add Banker sheet and Report dialog with ModalContext for back button handling
  useEffect(() => {
    if (!addDialogOpen) return;
    registerModal('sm-add-contact-sheet', () => setAddDialogOpen(false), 1);
    return () => unregisterModal('sm-add-contact-sheet');
  }, [addDialogOpen, registerModal, unregisterModal]);

  useEffect(() => {
    if (!reportDialogOpen) return;
    registerModal('sm-report-dialog', () => setReportDialogOpen(false), 1);
    return () => unregisterModal('sm-report-dialog');
  }, [reportDialogOpen, registerModal, unregisterModal]);

  const filteredContacts = searchItems(
    searchQuery,
    bankers,
    ['name', 'bank', 'phone', 'region', 'email', 'designation', 'remark', 'products']
  )
  .filter(c => filterBanks.length === 0 || filterBanks.includes(c.bank))
  .filter(c => filterProducts.length === 0 || filterProducts.some(p => c.products?.includes(p)))
  .filter(c => filterLocations.length === 0 || filterLocations.includes(c.region))
  .sort((a, b) => {
    // Sort favorites first
    const aFav = isFavorite(a.id);
    const bFav = isFavorite(b.id);
    if (aFav && !bFav) return -1;
    if (!aFav && bFav) return 1;
    return a.name.localeCompare(b.name);
  });

  const favoriteCount = bankers.filter(b => isFavorite(b.id)).length;

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto pb-24 md:pb-8">
        {/* Header with Add Banker Button */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/tools")} className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <PageHeader title="Banker Contact" subtitle="Bank Sales Manager contacts" />
          </div>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="shrink-0 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
            size="sm"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            <span>Add Banker</span>
          </Button>
        </div>

        <div className="space-y-3">
          {/* Row 1: Search bar + Filter toggle button */}
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search name, bank, phone, region..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 relative h-10 px-3 gap-1.5"
              onClick={() => setFilterExpanded(!filterExpanded)}
            >
              <SlidersHorizontal className="h-4 w-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 h-4.5 w-4.5 min-w-[18px] min-h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center font-bold leading-none px-0.5">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </div>

          {/* Row 2: Filter dropdowns (collapsed by default) */}
          {filterExpanded && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="flex gap-2 items-center flex-wrap pb-2 border-b"
            >
              {/* Bank Filter Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="shrink-0 gap-1.5 h-9">
                    <span className="text-xs">Bank</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <div className="p-2 border-b" onClick={(e) => e.stopPropagation()}>
                    <Input
                      placeholder="Search banks..."
                      value={bankSearch}
                      onChange={(e) => setBankSearch(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {config.banks
                      .filter(bank => bankers.some(b => b.bank === bank) && bank.toLowerCase().includes(bankSearch.toLowerCase()))
                      .map(bank => (
                        <DropdownMenuCheckboxItem
                          key={bank}
                          checked={filterBanks.includes(bank)}
                          onCheckedChange={(checked) => {
                            setFilterBanks(prev => checked ? [...prev, bank] : prev.filter(x => x !== bank));
                          }}
                          className="cursor-pointer"
                        >
                          <span className="text-sm flex-1">{bank}</span>
                          <span className="text-xs text-muted-foreground ml-auto">({bankers.filter(b => b.bank === bank).length})</span>
                        </DropdownMenuCheckboxItem>
                      ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Loan Type Filter Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="shrink-0 gap-1.5 h-9">
                    <span className="text-xs">Loan Type</span>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="w-56">
                  <div className="p-2 border-b" onClick={(e) => e.stopPropagation()}>
                    <Input
                      placeholder="Search types..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {config.products
                      .filter(product => bankers.some(b => b.products?.includes(product)) && product.toLowerCase().includes(productSearch.toLowerCase()))
                      .map(product => (
                        <DropdownMenuCheckboxItem
                          key={product}
                          checked={filterProducts.includes(product)}
                          onCheckedChange={(checked) => {
                            setFilterProducts(prev => checked ? [...prev, product] : prev.filter(x => x !== product));
                          }}
                          className="cursor-pointer"
                        >
                          <span className="text-sm flex-1">{product}</span>
                          <span className="text-xs text-muted-foreground ml-auto">({bankers.filter(b => b.products?.includes(product)).length})</span>
                        </DropdownMenuCheckboxItem>
                      ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Location Filter Dropdown */}
              {uniqueLocations.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="shrink-0 gap-1.5 h-9">
                      <span className="text-xs">Location</span>
                      <ChevronDown className="h-3.5 w-3.5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="p-2 border-b" onClick={(e) => e.stopPropagation()}>
                      <Input
                        placeholder="Search locations..."
                        value={locationSearch}
                        onChange={(e) => setLocationSearch(e.target.value)}
                        className="h-8 text-xs"
                      />
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {uniqueLocations
                        .filter(location => location.toLowerCase().includes(locationSearch.toLowerCase()))
                        .map(location => (
                          <DropdownMenuCheckboxItem
                            key={location}
                            checked={filterLocations.includes(location)}
                            onCheckedChange={(checked) => {
                              setFilterLocations(prev => checked ? [...prev, location] : prev.filter(x => x !== location));
                            }}
                            className="cursor-pointer"
                          >
                            <span className="text-sm flex-1">{location}</span>
                            <span className="text-xs text-muted-foreground ml-auto">({bankers.filter(b => b.region === location).length})</span>
                          </DropdownMenuCheckboxItem>
                        ))}
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </motion.div>
          )}

          {/* Stats + active filter badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="secondary">{filteredContacts.length} contacts</Badge>
            <Badge variant="accent">{favoriteCount} favorites</Badge>
            {filterBanks.map(b => (
              <Badge
                key={b}
                variant="outline"
                className="cursor-pointer gap-1 text-xs hover:bg-destructive/5 hover:border-destructive/30"
                onClick={() => setFilterBanks(prev => prev.filter(x => x !== b))}
              >
                {b} <X className="h-2.5 w-2.5" />
              </Badge>
            ))}
            {filterProducts.map(p => (
              <Badge
                key={p}
                variant="outline"
                className="cursor-pointer gap-1 text-xs hover:bg-destructive/5 hover:border-destructive/30"
                onClick={() => setFilterProducts(prev => prev.filter(x => x !== p))}
              >
                {p} <X className="h-2.5 w-2.5" />
              </Badge>
            ))}
            {filterLocations.map(l => (
              <Badge
                key={l}
                variant="outline"
                className="cursor-pointer gap-1 text-xs hover:bg-destructive/5 hover:border-destructive/30"
                onClick={() => setFilterLocations(prev => prev.filter(x => x !== l))}
              >
                {l} <X className="h-2.5 w-2.5" />
              </Badge>
            ))}
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          )}

          {/* Cards Grid - Similar to Channel Partners */}
          {!loading && (
            <div className="space-y-3">
              {filteredContacts.map((contact, index) => {
                const isUserFavorite = isFavorite(contact.id);
                return (
                  <motion.div key={contact.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.03 }}>
                    <Card 
                      variant="interactive"
                      className={cn(
                        "cursor-pointer overflow-hidden",
                        isUserFavorite && "border-gold/30 bg-gold/5",
                        contact.isReported && "border-warning/50 bg-warning/5"
                      )}
                      onClick={() => navigate(`/tools/sm-contact/${contact.id}`)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Avatar */}
                          <div className="relative flex-shrink-0">
                            <div className="flex flex-col items-center gap-1">
                              <div className="relative">
                                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent font-bold text-lg">
                                  {contact.name.charAt(0)}
                                </div>
                                {isUserFavorite && (
                                  <div className="absolute -top-1 -right-1 bg-gold rounded-full p-0.5">
                                    <Star className="h-3 w-3 fill-gold-foreground text-gold-foreground" />
                                  </div>
                                )}
                              </div>
                              {/* Leads & Reminders count badges - below avatar */}
                              {(getLeadCount(contact.id) > 0 || getReminderCount(contact.id) > 0) && (
                                <div className="flex flex-col items-center gap-1 mt-1">
                                  {getLeadCount(contact.id) > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-accent">
                                      <Users className="h-3.5 w-3.5" />
                                      <span className="font-semibold">{getLeadCount(contact.id)}</span>
                                    </div>
                                  )}
                                  {getReminderCount(contact.id) > 0 && (
                                    <div className="flex items-center gap-1 text-xs text-orange-500">
                                      <Bell className="h-3.5 w-3.5" />
                                      <span className="font-semibold">{getReminderCount(contact.id)}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0 space-y-1">
                            {/* Header: Name + Designation + Star/Report */}
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="font-semibold truncate">{contact.name}</h3>
                                  {contact.isReported && <Badge variant="destructive" className="text-xs">Reported</Badge>}
                                </div>
                                {(contact.designation || contact.region) && (
                                  <p className="text-xs text-muted-foreground truncate">
                                    {[contact.designation, contact.region].filter(Boolean).join(' • ')}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-col items-center gap-0 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  onClick={(e) => { e.stopPropagation(); handleToggleFavorite(e, contact.id); }}
                                  className={cn(
                                    "flex-shrink-0 h-7 w-7",
                                    isUserFavorite && "text-gold"
                                  )}
                                >
                                  <Star className={cn("h-4 w-4", isUserFavorite && "fill-gold")} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon-sm"
                                  className="flex-shrink-0 h-7 w-7 text-muted-foreground hover:text-destructive"
                                  onClick={(e) => handleOpenReport(e, contact.id)}
                                  title="Report"
                                >
                                  <AlertTriangle className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>

                            {/* Bank row */}
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <Badge variant="secondary" className="text-xs">{contact.bank}</Badge>
                            </div>

                            {/* Products */}
                            {contact.products && contact.products.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {contact.products.slice(0, 3).map(p => (
                                  <Badge key={p} variant="outline" className="text-xs py-0">
                                    {p}
                                  </Badge>
                                ))}
                                {contact.products.length > 3 && <Badge variant="outline" className="text-xs py-0">+{contact.products.length - 3}</Badge>}
                              </div>
                            )}

                            {/* Actions */}
                            <div className="flex items-center gap-2 pt-0.5">
                              <Button
                                variant="secondary"
                                size="sm"
                                className="h-8 px-3"
                                onClick={(e) => { e.stopPropagation(); window.open(`tel:${contact.phone}`); }}
                              >
                                <Phone className="h-3.5 w-3.5 mr-1" />
                                Call
                              </Button>
                              <Button
                                variant="success"
                                size="sm"
                                className="h-8 px-3"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const n = contact.phone.replace(/\D/g, '');
                                  window.open(`https://wa.me/${n.startsWith('91') && n.length > 10 ? n : '91' + n}`);
                                }}
                              >
                                <MessageCircle className="h-3.5 w-3.5 mr-1" />
                                WhatsApp
                              </Button>
                              <div className="flex-1" />
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          )}

          {!loading && filteredContacts.length === 0 && (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No contacts found</p>
              <Button variant="link" onClick={() => setAddDialogOpen(true)} className="mt-2">Add your first Banker contact</Button>
            </div>
          )}
        </div>

        {/* Add Contact Sheet - Mobile Optimized */}
        <Sheet open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <SheetContent side="bottom" className="h-[90vh] rounded-t-3xl flex flex-col">
            <SheetHeader className="pb-4 px-4 flex-shrink-0">
              <div className="flex items-center justify-center gap-2">
                <SheetTitle className="text-xl font-bold">Add Banker Contact</SheetTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-lg shrink-0"
                  title="Clear form"
                  onClick={() => {
                    setNewContact({ name: "", bank: "", phone: "", email: "", region: "", designation: "", products: [], remark: "" });
                    setNewContactLocation(null);
                    setIsOtherDesignation(false);
                    setCustomDesignation('');
                    fileManagerReset();
                    toast.info("Form cleared");
                  }}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </SheetHeader>
            
            <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-4 pb-8">
              {/* SECTION: Basic Information */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                  <User className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-sm text-green-800">Basic Information</h3>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Name *</Label>
                  <Input value={newContact.name} onChange={(e) => setNewContact(p => ({ ...p, name: e.target.value }))} placeholder="Ex - Rajesh Kumar" className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Bank *</Label>
                  <SearchableSelect
                    options={bankOptions}
                    value={newContact.bank}
                    onValueChange={(value) => setNewContact(p => ({ ...p, bank: value }))}
                    placeholder="Search bank..."
                    className="h-12"
                  />
                </div>
              </div>

              {/* SECTION: Contact Details */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                  <Phone className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-sm text-green-800">Contact Details</h3>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Phone *</Label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">+91</span>
                    <Input value={newContact.phone} onChange={(e) => setNewContact(p => ({ ...p, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} placeholder="9876543210" inputMode="numeric" maxLength={10} className="h-12 rounded-l-none" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Email</Label>
                  <Input value={newContact.email} onChange={(e) => setNewContact(p => ({ ...p, email: e.target.value }))} placeholder="Ex - name@email.com" type="email" className="h-12" />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Region</Label>
                  <SearchableSelect
                    options={cityOptions}
                    value={newContact.region}
                    onValueChange={(value) => setNewContact(p => ({ ...p, region: value }))}
                    placeholder="Search city..."
                    className="h-12"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Designation</Label>
                  <Select 
                    value={isOtherDesignation ? 'Other' : (DESIGNATION_OPTIONS.includes(newContact.designation) ? newContact.designation : (newContact.designation ? 'Other' : ''))}
                    onValueChange={(value) => {
                      if (value === 'Other') {
                        setIsOtherDesignation(true);
                        setCustomDesignation('');
                        setNewContact(p => ({ ...p, designation: '' }));
                      } else {
                        setIsOtherDesignation(false);
                        setCustomDesignation('');
                        setNewContact(p => ({ ...p, designation: value }));
                      }
                    }}
                  >
                    <SelectTrigger className="h-12">
                      <SelectValue placeholder="Select designation" />
                    </SelectTrigger>
                    <SelectContent>
                      {DESIGNATION_OPTIONS.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isOtherDesignation && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Enter Designation *</Label>
                    <Input
                      value={customDesignation}
                      onChange={(e) => { setCustomDesignation(e.target.value); setNewContact(p => ({ ...p, designation: e.target.value })); }}
                      placeholder="e.g. Branch Manager, Credit Head..."
                      className="h-12 placeholder:text-muted-foreground/50"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* SECTION: Products */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                  <CreditCard className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-sm text-green-800">Products</h3>
                </div>
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {config.products.map(product => (
                    <div key={product} className="flex items-center space-x-2">
                      <Checkbox id={product} checked={newContact.products.includes(product)} onCheckedChange={() => handleProductToggle(product)} />
                      <label htmlFor={product} className="text-sm cursor-pointer">{product}</label>
                    </div>
                  ))}
                </div>
              </div>
              </div>

              {/* SECTION: Notes */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                  <FileText className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-sm text-green-800">Notes</h3>
                </div>
              <div className="space-y-2">
                <Textarea value={newContact.remark} onChange={(e) => setNewContact(p => ({ ...p, remark: e.target.value }))} placeholder="Any notes..." rows={2} className="resize-none" />
              </div>
              </div>

              {/* SECTION: Location */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                  <MapPin className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-sm text-green-800">Location</h3>
                </div>
                <div className="space-y-1.5">
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="flex-1 h-12"
                      onClick={() => setLocationPickerOpen(true)}
                    >
                      <Map className="h-4 w-4 mr-2 shrink-0" />
                      {newContactLocation ? "Location Set ✓ — Change" : "Pick Location on Map"}
                    </Button>
                  </div>
                  {newContactLocation && (
                    <p className="text-xs text-muted-foreground pl-1">📍 {newContactLocation.lat.toFixed(6)}, {newContactLocation.lng.toFixed(6)}</p>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2 border-b border-green-100 pb-2">
                  <FileText className="h-4 w-4 text-green-600" />
                  <h3 className="font-semibold text-sm text-green-800">Documents</h3>
                </div>
              <FileUploadSection
                files={fileManagerFiles}
                onCameraCapture={fileUploadManager.handleCameraCapture}
                onFileUpload={fileUploadManager.handleFileUpload}
                onRemoveFile={fileUploadManager.handleRemovePendingFile}
                onImageClick={fileUploadManager.openImageViewer}
                imageViewerState={{
                  isOpen: fileUploadManager.imageViewerState.isOpen,
                  images: fileUploadManager.imageViewerState.images,
                  currentIndex: fileUploadManager.imageViewerState.currentIndex,
                  setCurrentIndex: fileUploadManager.imageViewerState.setCurrentIndex,
                  onClose: fileUploadManager.closeImageViewer,
                }}
              />
              </div>
              
              <Button 
                onClick={handleAddContact} 
                disabled={isSubmitting}
                className="w-full h-12 mt-4 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-semibold"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Contact'
                )}
              </Button>
            </div>
            </div>
          </SheetContent>
        </Sheet>

      <MapLocationPicker
        open={locationPickerOpen}
        onOpenChange={setLocationPickerOpen}
        initialLocation={newContactLocation}
        onLocationSelect={(loc) => {
          setNewContactLocation(loc);
          toast.success("Location selected from map");
        }}
      />

      {/* Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-xl font-bold">
                <AlertTriangle className="h-5 w-5 text-warning" />
                Report SM
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                Submit a report if this Banker contact is no longer valid or has issues.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Report Reason *</Label>
                <Textarea 
                  value={reportReason} 
                  onChange={(e) => setReportReason(e.target.value)} 
                  placeholder="e.g., Banker left job, incorrect contact details..." 
                  rows={4}
                  className="resize-none"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <Button 
                  variant="outline" 
                  onClick={() => setReportDialogOpen(false)} 
                  className="flex-1 h-11 font-semibold"
                >
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleSubmitReport}
                  disabled={isSubmitting}
                  className="flex-1 h-11 font-semibold"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Report'
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
